import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() { return this.closest('theme-drawer'); }
  get #dialog() { return this.closest('dialog'); }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #isMealPlanAdd(event) {
    const onHomepage = Boolean(document.querySelector('#MainContent[data-template="index"]'));
    const onMealPlansPage = window.location.pathname.replace(/\/$/, '') === '/pages/meal-plans';
    if (!onHomepage && !onMealPlansPage) return false;

    const mealPlanProductIds = new Set(['9375304810711','9375304876247','9375304909015','9375304941783','9375304974551','9375305007319']);
    const mealPlanVariantIds = new Set(['49629047980247','49629048078551','49629048111319','49629048144087','49629048176855','49629048209623']);

    const target = event?.target;
    if (target instanceof Element) {
      const productForm = target.closest('product-form-component');
      const productId = productForm?.getAttribute('data-product-id');
      if (productId && mealPlanProductIds.has(productId)) return true;
    }

    const lines = Array.isArray(event?.lines) ? event.lines : [];
    if (lines.some((line) => mealPlanVariantIds.has(String(line?.merchandiseId || '').replace('gid://shopify/ProductVariant/','')))) return true;

    const detailProductId = String(event?.detail?.productId || '').replace('gid://shopify/Product/','');
    return Boolean(detailProductId && mealPlanProductIds.has(detailProductId));
  }

  #handleCartLinesUpdate = (event) => {
    const suppressAutoOpen = this.#isMealPlanAdd(event);
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen && !suppressAutoOpen;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) this.#themeDrawer?.open();

    event.promise?.then(({ detail }) => {
      const settle = () => requestAnimationFrame(() => this.#updateStickyState());
      if (suppressAutoOpen || !shouldAutoOpen || detail?.didError) { settle(); return; }
      const openAndSettle = () => { if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open(); settle(); };
      if (sourceModal?.open) sourceModal.addEventListener('close', openAndSettle, { once: true });
      else openAndSettle();
    }).catch((error) => { if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error); });
  };

  #isCartEmpty() { return Boolean(this.querySelector('.cart-drawer--empty')); }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) { dialog.setAttribute('cart-summary-sticky', 'false'); return; }
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    dialog.setAttribute('cart-summary-sticky', summaryHeight / drawerHeight > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) customElements.define('cart-drawer-component', CartDrawerComponent);
